import { useEffect, useState } from 'react';
import { t } from '../../platform/lang';

/**
 * Ask the driver to install the app to their home screen.
 *
 * This should have existed weeks ago and did not, and it is probably the single
 * largest reliability win available without going near an app store.
 *
 * A web page in a browser TAB is the weakest thing on a phone. When Spotify or
 * YouTube or a game wants memory, the operating system takes it from the
 * browser first, and the tab is destroyed. That is exactly what the 'died'
 * reports were recording: the app was not crashing, it was being evicted.
 *
 * An INSTALLED web app is treated differently. It gets its own process and its
 * own memory budget, and the OS ranks it with real applications rather than
 * with browser tabs. Same code, same URL, no developer account, no store, no
 * review — it is simply a better container for the identical app.
 *
 * You cannot ask the OS for a memory allowance; there is no such API. Installing
 * is the whole of the control we have, which is why it is worth asking for.
 *
 * The two platforms differ in one way that matters:
 *
 *   Android — fires `beforeinstallprompt`, which we hold and fire from a
 *             button. One tap and it is installed.
 *   iOS     — Apple does not allow a site to trigger installation. The driver
 *             must use Share, then "Add to Home Screen". So on an iPhone this
 *             can only SHOW them how, which is why it is written as two short
 *             steps rather than a button that does nothing.
 */

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const HIDDEN_KEY = 'jw.installHidden';
/** Written by InstallGate when a driver puts it off for the day. */
const SKIPPED_KEY = 'jw.installSkippedOn';

function skippedToday(): boolean {
  try {
    return localStorage.getItem(SKIPPED_KEY) === new Date().toDateString();
  } catch { return false; }
}

/** Already installed? Then there is nothing to ask for. */
export function isInstalled(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function InstallPrompt() {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDDEN_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Stop Chrome showing its own bar; we ask in the driver's own language.
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  /*
   * Never twice in one session. The full-screen gate asks first; if the driver
   * said they could not do it, this banner asking again on the very next screen
   * is nagging, not helping. It comes back tomorrow, from the gate.
   */
  if (isInstalled() || hidden || skippedToday()) return null;
  /*
   * Android shows a one-tap button here. iPhones get nothing in this banner --
   * the instructions need room and pictures, and they live on the full-screen
   * step (InstallGate) and in the guide. A cramped banner repeating them would
   * be the confusing version the owner rejected.
   */
  if (!event) return null;

  const dismiss = () => {
    try { localStorage.setItem(HIDDEN_KEY, '1'); } catch { /* fine */ }
    setHidden(true);
  };

  return (
    <div className="banner install-banner">
      <p><b>{t('installTitle')}</b></p>
      <p>{t('installWhy')}</p>
      {event ? (
        <button className="btn primary" onClick={() => {
          void (async () => {
            try {
              await event.prompt();
              const { outcome } = await event.userChoice;
              if (outcome === 'accepted') dismiss();
            } catch { /* the driver can try again next time */ }
          })();
        }}>
          {t('installNow')}
        </button>
      ) : (
        <p className="hint">{t('installIos')}</p>
      )}
      <button className="btn link" onClick={dismiss}>{t('installLater')}</button>
    </div>
  );
}
